/**
 * Typed error envelope for the contacts / signing-keys REST surface.
 *
 * Per FD `aidlc-docs/construction/contacts/functional-design/pr-c-dashboard.md § 1.5`:
 * the API emits a flat envelope (`{ error, message, request_id, details? }`)
 * — `ApiError` parses that shape and surfaces it to React Query callers.
 */

export type ApiErrorDetails = Readonly<Record<string, unknown>>;

interface ApiErrorEnvelope {
  readonly error?: string;
  readonly message?: string;
  readonly request_id?: string;
  readonly details?: ApiErrorDetails;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details?: ApiErrorDetails;

  constructor(args: {
    status: number;
    code: string;
    message: string;
    requestId: string;
    details?: ApiErrorDetails;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.status = args.status;
    this.code = args.code;
    this.requestId = args.requestId;
    this.details = args.details;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }
  isUnauthenticated(): boolean {
    return this.status === 401;
  }
  isForbidden(): boolean {
    return this.status === 403;
  }
  isConflict(): boolean {
    return this.status === 409;
  }
  isRateLimited(): boolean {
    return this.status === 429;
  }
  isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/** Parse the flat envelope into an `ApiError`. Tolerates missing fields. */
export function parseApiError(status: number, body: unknown, statusText?: string): ApiError {
  const env = (body ?? {}) as ApiErrorEnvelope;
  return new ApiError({
    status,
    code: env.error ?? `http_${status}`,
    message: env.message ?? statusText ?? "Request failed",
    requestId: env.request_id ?? "",
    details: env.details,
  });
}
